import React, { useState, useEffect } from "react";
import { Button, Select, InputNumber, Switch } from "antd";
import { SettingOutlined } from "@ant-design/icons";

import "../../css/freemium.css";
import BiofuelCompositionModal from "./fuelModals/BiofuelCompositionModal";
import IceClassModal from "./fuelModals/IceClassModal";

const { Option } = Select;

const FreemiumEventsCard = ({
  onFuelChange,
  onBiofuelChange,
  onYearChange,
  onIceClassChange,
  onIceClassDataChange,
}) => {
  const [selectedYear, setSelectedYear] = useState("2026");
  const [biofuelModalVisible, setBiofuelModalVisible] = useState(false);
  const [activeBiofuelId, setActiveBiofuelId] = useState(null);
  const [isIceClass, setIsIceClass] = useState(false);
  const [iceClassModalVisible, setIceClassModalVisible] = useState(false);
  const [iceClassData, setIceClassData] = useState({
    iceClassType: undefined,
    iceDistance: undefined,
  });
  const [biofuelConfigs, setBiofuelConfigs] = useState({});

  const [displayFuelData, setDisplayFuelData] = useState([
    {
      id: Date.now(),
      fuel_name: "",
      fuel_class: "Fossil",
      sea_consumption: "",
      port_consumption: "",
      uk_port_consumption: "",
      ice_class_consumption: "",
      isBiofuel: false,
    },
  ]);

  const handleFuelTypeSelect = (value) => {
    setDisplayFuelData((prev) => [
      ...prev,
      {
        id: Date.now(),
        fuel_name: "",
        fuel_class: value,
        sea_consumption: "",
        port_consumption: "",
        uk_port_consumption: "",
        ice_class_consumption: isIceClass ? 0 : "",
        isBiofuel: value === "Biofuel",
      },
    ]);
  };

  const handleDeleteFuel = (id) => {
    if (displayFuelData.length === 1) return;
    setDisplayFuelData((prev) => prev.filter((f) => f.id !== id));
    setBiofuelConfigs((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleIceClassToggle = (checked) => {
    if (checked) {
      setIceClassModalVisible(true);
    } else {
      setIsIceClass(false);
      setIceClassData({ iceClassType: undefined, iceDistance: undefined });
      setDisplayFuelData((prev) =>
        prev.map((fuel) => ({ ...fuel, ice_class_consumption: "" }))
      );
    }
  };

  const handleIceClassSubmit = (data) => {
    setIceClassData(data);
    setIsIceClass(true);
    setDisplayFuelData((prev) =>
      prev.map((fuel) => ({ ...fuel, ice_class_consumption: 0 }))
    );
    setIceClassModalVisible(false);
  };

  const handleIceClassModalClose = () => {
    if (!isIceClass) setIsIceClass(false);
    setIceClassModalVisible(false);
  };

  const handleBiofuelSubmit = ({ formData }) => {
    setBiofuelConfigs((prev) => ({ ...prev, [activeBiofuelId]: formData }));
    setDisplayFuelData((prev) =>
      prev.map((fuel) =>
        fuel.id === activeBiofuelId
          ? { ...fuel, fuel_name: formData.biofuelName }
          : fuel
      )
    );
    setBiofuelModalVisible(false);
    setActiveBiofuelId(null);
  };

  useEffect(() => {
    const fossilFuels = displayFuelData.filter((f) => f.fuel_class === "Fossil");
    onFuelChange?.(fossilFuels);
  }, [displayFuelData, onFuelChange]);

  useEffect(() => {
    const biofuelRows = displayFuelData.filter((f) => f.isBiofuel);
    const allBiofuelData = biofuelRows
      .map((biofuel) => {
        const config = biofuelConfigs[biofuel.id];
        if (!config) return null;
        return {
          ...config,
          sea_consumption: biofuel.sea_consumption || 0,
          port_consumption: biofuel.port_consumption || 0,
          uk_port_consumption: biofuel.uk_port_consumption || 0,
          ice_class_consumption: biofuel.ice_class_consumption || 0,
        };
      })
      .filter(Boolean);
    onBiofuelChange?.(allBiofuelData);
  }, [biofuelConfigs, displayFuelData, onBiofuelChange]);

  useEffect(() => { onYearChange?.(selectedYear); }, [selectedYear, onYearChange]);
  useEffect(() => { onIceClassChange?.(isIceClass); }, [isIceClass, onIceClassChange]);
  useEffect(() => { onIceClassDataChange?.(iceClassData); }, [iceClassData, onIceClassDataChange]);

  const updateFuel = (id, field, value) =>
    setDisplayFuelData((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );

  return (
    <div className="freemium-right-card">
      <div className="fuel-header">
        <h3 className="card-title">Fuel Consumption Information</h3>
        <div className="fuel-header-actions fuel-header-center">
          <h3 className="YFilter">Year<span style={{ color: "red" }}>*</span> :</h3>
          <Select value={selectedYear} onChange={setSelectedYear} className="selectors">
            <Option value="2025">2025</Option>
            <Option value="2026">2026</Option>
          </Select>
          <div className="rightpart">
            <Select
              placeholder="+ Add Fuel"
              className="add-fuel-dropdown"
              style={{ width: 150 }}
              onChange={handleFuelTypeSelect}
              value={null}
            >
              <Option value="Fossil">Fossil</Option>
              <Option value="Biofuel">Biofuel</Option>
            </Select>
            <div className="ice-toggle">
              <span>Ice Class &nbsp;</span>
              <Switch checked={isIceClass} onChange={handleIceClassToggle} />
            </div>
          </div>
        </div>
      </div>

     

      <div className="fuel-list">
        {displayFuelData.map((fuel) => (
          <div key={fuel.id} className={`fuel-row ${fuel.isBiofuel ? "biofuel-row" : ""}`}>
            <div className="fuel-info-section">
              <Button
                type="text"
                danger
                disabled={displayFuelData.length === 1}
                onClick={() => handleDeleteFuel(fuel.id)}
                className="delete-fuel-btn"
              >
                ✕
              </Button>

              <div className="fuel-name-wrapper">
                {fuel.fuel_class === "Fossil" ? (
                  <>
                    <Select
                      value={fuel.fuel_name}
                      placeholder="Select fuel"
                      style={{ width: 140 }}
                      listHeight={280}
                      virtual={false}
                      getPopupContainer={() => document.body}
                      onChange={(value) => updateFuel(fuel.id, 'fuel_name', value)}
                    >
                      <Option value="HFO">HFO</Option>
                      <Option value="VLSFO">VLSFO</Option>
                      <Option value="ULSFO">ULSFO</Option>
                      <Option value="LFO">LFO</Option>
                      <Option value="MDO">MDO</Option>
                      <Option value="LPG-AVERAGE">LPG-AVERAGE</Option>
                      <Option value="LNG-BOILER">LNG-BOILER</Option>
                      <Option value="LNG-OTHERS">LNG-OTHERS</Option>
                      <Option value="LNG-AE">LNG-AE</Option>
                      <Option value="LNG-ME">LNG-ME</Option>
                      <Option value="LPG-BUTANE">LPG-BUTANE</Option>
                      <Option value="LPG-PROPANE">LPG-PROPANE</Option>
                      <Option value="METHANOL">METHANOL</Option>
                      <Option value="ETHANOL">ETHANOL</Option>
                    </Select>
                    <span className="fuel-types red"> | Fossil</span>
                  </>
                ) : (
                  <div className="biofuel-display">
                    <span className="biofuel-name">{fuel.fuel_name || "Configure Biofuel"}</span>
                    <span className="fuel-types green"> | Biofuel</span>
                    <Button
                      type="text"
                      icon={<SettingOutlined />}
                      onClick={() => {
                        setActiveBiofuelId(fuel.id);
                        setBiofuelModalVisible(true);
                      }}
                      className="biofuel-settings-btn"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="fuel-consumption-inputs">
              <InputNumber
                placeholder="Sea"
                min={0}
                value={fuel.sea_consumption}
                onChange={(v) => updateFuel(fuel.id, 'sea_consumption', v)}
              />

              <InputNumber
                placeholder="Port (EU Port only)"
                min={0}
                value={fuel.port_consumption}
                onChange={(v) => updateFuel(fuel.id, 'port_consumption', v)}
              />

              {/* NEW: UK Port field */}
              <InputNumber
                placeholder="Port (UK Port only)"
                min={0}
                value={fuel.uk_port_consumption}
                onChange={(v) => updateFuel(fuel.id, 'uk_port_consumption', v)}
              />

              <InputNumber
                placeholder="Ice"
                min={0}
                disabled={!isIceClass}
                value={isIceClass ? fuel.ice_class_consumption : undefined}
                onChange={(v) => updateFuel(fuel.id, 'ice_class_consumption', v || 0)}
              />
            </div>
          </div>
        ))}
      </div>

      <BiofuelCompositionModal
        visible={biofuelModalVisible}
        onClose={() => setBiofuelModalVisible(false)}
        onSubmit={handleBiofuelSubmit}
        selectedYear={selectedYear}
      />

      <IceClassModal
        visible={iceClassModalVisible}
        onClose={handleIceClassModalClose}
        onSubmit={handleIceClassSubmit}
        initialData={iceClassData}
      />
    </div>
  );
};

export default FreemiumEventsCard;
